import { Alert, Button, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useNotification } from '../../../../hooks/NotificationContext';
import { fetchAssociatedIssues } from '../../../../modules/associations';
import { createIssueLinks } from '../../../../modules/jira';
import MultiSelectDropdown from '../../MultiSelectDropdown';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QUERY_KEYS from '../../../../query/queryKeys';
import { RefreshButton } from '../../CustomButtons';

const JiraIssueLinkage = ({
  testCaseId,
  issueKey,
  projectId,
  handleModalClose,
  type,
  setSubmitting,
}) => {
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [submittingLink, setSubmittingLink] = useState(false);
  const [error, setError] = useState(null);
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const forceRefreshRef = useRef(false);

  const {
    data: linkedIssuesOptions = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.testCase.linkedIssues, testCaseId],
    queryFn: async () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const response = await fetchAssociatedIssues(
        testCaseId,
        projectId,
        forceRefresh
      );
      return (
        response?.issues?.map((issue) => ({
          name: `${issue.key} - ${issue.summary}`,
          id: issue.key,
        })) || []
      );
    },
    enabled: !!testCaseId,
  });

  const handleRefresh = async () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({
      queryKey: [QUERY_KEYS.testCycle.linkedIssues, testCaseId],
      exact: true,
    });
  };

  const handleLinkIssues = async () => {
    setError(null);
    if (!issueKey || selectedIssues.length === 0) {
      setError('Please select issues to link.');
      return;
    }

    const issueKeys = Array.isArray(issueKey) ? issueKey : [issueKey];
    const duplicateIssues = issueKeys.filter(key => selectedIssues.includes(key));
    if (duplicateIssues.length > 0) {
        setError(`You can't link issue(s) ${duplicateIssues.join(', ')} to itself.`);
        return;
    }

    setSubmittingLink(true);
    setSubmitting(true);
    try {
      const payload = {
        issueKey,
        selectedIssues,
      };
      await createIssueLinks(payload, projectId);
      setSelectedIssues([]);
      addNotification('success', 'Issues linked successfully.');
      handleClose();
    } catch (err) {
      setError('Failed to link issues. ' + err.message);
    } finally {
      setSubmitting(false);
      setSubmittingLink(false);
    }
  };

  const handleClose = () => {
    setSelectedIssues([]);
    setError(null);
    handleModalClose();
  };

  return (
    <>
      {error && (
        <Alert severity="error" className="mb-2">
          {error}
        </Alert>
      )}
      <div className="d-flex justify-content-between">
        <Typography variant="body2" className="mb-2">
          You can link the{' '}
          <strong>
            {type === 'create' ? 'newly created issue' : 'selected issues'}
          </strong>{' '}
          ({Array.isArray(issueKey) ? issueKey.join(', ') : issueKey}) to
          existing Jira issues that are already associated with this test case.
        </Typography>
        {!isLoading && (
          <RefreshButton
            onRefresh={handleRefresh}
            isFetching={isFetching}
            showLastUpdated={false}
            buttonSize={24}
          />
        )}
      </div>
      {isLoading ? (
        <p className="text-center text-muted mt-2">Fetching issues...</p>
      ) : linkedIssuesOptions.length > 0 ? (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Select Jira Issues to Link</Form.Label>
            <MultiSelectDropdown
              options={linkedIssuesOptions}
              selectedValues={selectedIssues}
              onChange={setSelectedIssues}
              placeholder="Choose issues to link"
              showSelectAll
              name='issues'
            />
          </Form.Group>

          <div className="text-center mt-3">
            <Button
              variant="contained"
              color="primary"
              onClick={handleLinkIssues}
              disabled={submittingLink || selectedIssues.length === 0}>
              {submittingLink ? 'Linking issues...' : 'Link Issues'}
            </Button>
          </div>
        </>
      ) : (
        <Typography
          variant="body2"
          className="my-3"
          color="secondary"
          align="center">
          No Jira issues are currently linked to this test case.
        </Typography>
      )}
    </>
  );
};

export default JiraIssueLinkage;
